import { format } from 'date-fns';

/** Server-side maximum page size for the paged report endpoints. */
export const REPORT_EXPORT_PAGE_SIZE = 200;

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

type QueryValue = string | number | boolean | Date | undefined | null;

/**
 * Builds a query string for ASP.NET Core model binding: arrays are emitted as repeated
 * keys (`employeeIds=1&employeeIds=2`) and dates as `yyyy-MM-dd` (local calendar date).
 */
export function buildReportQueryString(params: Record<string, QueryValue | QueryValue[]>): string {
  const search = new URLSearchParams();
  const append = (key: string, value: QueryValue) => {
    if (value === undefined || value === null || value === '') return;
    search.append(key, value instanceof Date ? format(value, 'yyyy-MM-dd') : String(value));
  };

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) value.forEach((v) => append(key, v));
    else append(key, value);
  }

  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Fetches every page of a paged report query at the server's maximum page size and
 * returns the concatenated rows.
 */
export async function fetchAllPages<T>(
  fetchPage: (pageNumber: number, pageSize: number) => Promise<PagedResult<T>>,
  pageSize: number = REPORT_EXPORT_PAGE_SIZE,
): Promise<T[]> {
  const rows: T[] = [];
  let pageNumber = 1;
  for (;;) {
    const page = await fetchPage(pageNumber, pageSize);
    rows.push(...page.items);
    if (page.items.length < pageSize || rows.length >= page.totalCount) return rows;
    pageNumber++;
  }
}

/** HTTP status of an `ApiError`-shaped rejection, if any. */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}
